import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  CreateEventRequest,
  EventRecord,
  UpdateEventRequest,
} from "@/shared/domain/events";
import {
  eventQueryKeys,
  type EventsRequest,
} from "@/shared/domain/events/hooks";

import { useEphemeralWorkspaceState } from "./hooks";

interface SelectOptions {
  datasetIds?: string[];
  fileIds?: string[];
  topicIds?: string[];
  messagePathIds?: string[];
}

type UseWorkspaceEventsOptions = Partial<{
  select: SelectOptions;
}>;

export function useWorkspaceEvents(options?: UseWorkspaceEventsOptions) {
  const { events } = useDomainServices();

  const { files, topics, messagePaths, isDataLoading } =
    useEphemeralWorkspaceState();

  const request = React.useMemo((): EventsRequest => {
    return {
      datasetIds: files.map((file) => file.association_id),
      fileIds: files.map((file) => file.file_id),
      topicIds: topics.map((topic) => topic.topic_id),
      messagePathIds: messagePaths.map(
        (messagePath) => messagePath.message_path_id,
      ),
    };
  }, [files, topics, messagePaths]);

  return useQuery({
    queryKey: eventQueryKeys.forWorkspace(request),
    queryFn: ({ signal }) => {
      return events.getEventsForAssociations(request, {
        abortSignal: signal,
      });
    },
    select: (data) => {
      if (options?.select) {
        return selectEvents(data, options.select);
      }

      return data;
    },
    enabled: !isDataLoading,
    staleTime: 300_000, // 5 minutes
  });
}

export function useWorkspaceEvent(eventId: string) {
  const { events } = useDomainServices();

  return useQuery({
    queryKey: eventQueryKeys.detail(eventId),
    queryFn: ({ signal }) => {
      return events.getEventById(eventId, {
        abortSignal: signal,
      });
    },
    staleTime: 300_000, // 5 minutes
  });
}

export function useCreateWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();

  const orgId = useCurrentOrgId();
  const { files, topics, messagePaths } = useEphemeralWorkspaceState();

  return useMutation({
    mutationFn: function createWorkspaceEvent(request: CreateEventRequest) {
      return events.createEvent(orgId, request);
    },
    onSuccess: (event: EventRecord) => {
      const request = {
        datasetIds: files.map((file) => file.association_id),
        fileIds: files.map((file) => file.file_id),
        topicIds: topics.map((topic) => topic.topic_id),
        messagePathIds: messagePaths.map(
          (messagePath) => messagePath.message_path_id,
        ),
      };

      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(request),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return [...oldData, event];
        },
      );
    },
  });
}

export function useDeleteWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();

  const { files, topics, messagePaths } = useEphemeralWorkspaceState();

  return useMutation<void, Error, EventRecord["event_id"]>({
    mutationFn: function deleteWorkspaceEvent(eventId: string) {
      return events.deleteEvent(eventId);
    },
    onSuccess: (_, eventId) => {
      const request = {
        datasetIds: files.map((file) => file.association_id),
        fileIds: files.map((file) => file.file_id),
        topicIds: topics.map((topic) => topic.topic_id),
        messagePathIds: messagePaths.map(
          (messagePath) => messagePath.message_path_id,
        ),
      };

      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(request),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return oldData.filter((event) => event.event_id !== eventId);
        },
      );
    },
  });
}

export function useUpdateWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();

  const { files, topics, messagePaths } = useEphemeralWorkspaceState();

  return useMutation<
    EventRecord,
    Error,
    { eventId: string; request: UpdateEventRequest }
  >({
    mutationFn: function updateWorkspaceEvent({ eventId, request }) {
      return events.updateEvent(eventId, request);
    },
    onSuccess: (event: EventRecord) => {
      const request = {
        datasetIds: files.map((file) => file.association_id),
        fileIds: files.map((file) => file.file_id),
        topicIds: topics.map((topic) => topic.topic_id),
        messagePathIds: messagePaths.map(
          (messagePath) => messagePath.message_path_id,
        ),
      };

      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(request),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }

          return oldData.map((oldEvent) => {
            if (oldEvent.event_id === event.event_id) {
              return event;
            }
            return oldEvent;
          });
        },
      );

      queryClient.setQueryData(eventQueryKeys.detail(event.event_id), event);

      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(),
      });
    },
  });
}

function selectEvents(events: EventRecord[], select: SelectOptions) {
  return events.filter((event) => {
    const {
      datasetIds = [],
      fileIds = [],
      topicIds = [],
      messagePathIds = [],
    } = select;

    if (
      datasetIds.length > 0 &&
      event.associations.some((association) =>
        datasetIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      fileIds.length > 0 &&
      event.associations.some((association) =>
        fileIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      topicIds.length > 0 &&
      event.associations.some((association) =>
        topicIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      messagePathIds.length > 0 &&
      event.associations.some((association) =>
        messagePathIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    return false;
  });
}
